import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  Users, Plus, Edit, Trash2, Linkedin, Mail, Phone, Building2,
  User, Upload, GripVertical, ChevronRight, Loader2
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function AdminTeam() {
  const [members, setMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    designation: '',
    email: '',
    linkedin_url: '',
    overview: '',
    reports_to: '',
    department: '',
    phone: '',
    profile_image_url: '',
    is_active: true
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadMembers();
  }, []);

  const loadMembers = async () => {
    try {
      const res = await fetch(`${API_URL}/api/team/members?is_admin=true`);
      if (res.ok) {
        const data = await res.json();
        setMembers(data.members || []);
      }
    } catch (error) {
      console.error('Failed to load team members:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await fetch(`${API_URL}/api/team/members/upload-image`, {
        method: 'POST',
        body: formData
      });
      
      if (res.ok) {
        const data = await res.json();
        setFormData(prev => ({ ...prev, profile_image_url: data.url }));
        toast.success('Image uploaded');
      }
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.designation || !formData.email) {
      toast.error('Name, designation, and email are required');
      return;
    }
    
    setIsSubmitting(true);
    try {
      const url = editingMember 
        ? `${API_URL}/api/team/members/${editingMember.id}`
        : `${API_URL}/api/team/members`;
      
      // If reports_to is selected, get the name
      let reports_to_name = null;
      if (formData.reports_to) {
        const manager = members.find(m => m.id === formData.reports_to);
        reports_to_name = manager?.name;
      }
      
      const res = await fetch(url, {
        method: editingMember ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, reports_to_name })
      });
      
      if (res.ok) {
        toast.success(editingMember ? 'Member updated' : 'Member added');
        setShowDialog(false);
        setEditingMember(null);
        resetForm();
        loadMembers();
      }
    } catch (error) {
      toast.error('Failed to save member');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (memberId) => {
    try {
      const res = await fetch(`${API_URL}/api/team/members/${memberId}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        toast.success('Member deleted');
        loadMembers();
      }
    } catch (error) {
      toast.error('Failed to delete member');
    }
    setDeleteConfirm(null);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      designation: '',
      email: '',
      linkedin_url: '',
      overview: '',
      reports_to: '',
      department: '',
      phone: '',
      profile_image_url: '',
      is_active: true
    });
  };

  const openEditDialog = (member) => {
    setEditingMember(member);
    setFormData({
      name: member.name || '',
      designation: member.designation || '',
      email: member.email || '',
      linkedin_url: member.linkedin_url || '',
      overview: member.overview || '',
      reports_to: member.reports_to || '',
      department: member.department || '',
      phone: member.phone || '',
      profile_image_url: member.profile_image_url || '',
      is_active: member.is_active !== false
    });
    setShowDialog(true);
  };

  const openAddDialog = () => {
    setEditingMember(null);
    resetForm();
    setShowDialog(true);
  };

  const getImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `${API_URL}${url}`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/20 rounded-lg">
              <Users className="h-8 w-8" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Know the Team</h1>
              <p className="text-indigo-100 mt-1">
                Manage quality department team members
              </p>
            </div>
          </div>
          <Button onClick={openAddDialog} variant="secondary">
            <Plus className="h-4 w-4 mr-2" />
            Add Team Member
          </Button>
        </div>
      </div>

      {/* Team Members Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {members.length === 0 ? (
          <Card className="col-span-full">
            <CardContent className="text-center py-12">
              <Users className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
              <p className="text-muted-foreground">No team members yet</p>
              <Button onClick={openAddDialog} className="mt-4">
                <Plus className="h-4 w-4 mr-2" />
                Add First Member
              </Button>
            </CardContent>
          </Card>
        ) : (
          members.map((member) => (
            <Card key={member.id} className={`overflow-hidden ${!member.is_active ? 'opacity-60' : ''}`}>
              <div className="h-32 bg-gradient-to-br from-indigo-400 to-purple-500 relative">
                <div className="absolute -bottom-10 left-1/2 transform -translate-x-1/2">
                  <Avatar className="h-20 w-20 border-4 border-white shadow-lg">
                    <AvatarImage src={getImageUrl(member.profile_image_url)} alt={member.name} />
                    <AvatarFallback className="bg-indigo-100 text-indigo-600 text-xl">
                      {member.name?.charAt(0) || 'U'}
                    </AvatarFallback>
                  </Avatar>
                </div>
                {!member.is_active && (
                  <Badge variant="secondary" className="absolute top-2 right-2">Inactive</Badge>
                )}
              </div>
              
              <CardContent className="pt-14 text-center">
                <h3 className="font-semibold text-lg">{member.name}</h3>
                <p className="text-sm text-indigo-600 font-medium">{member.designation}</p>
                
                {member.department && (
                  <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground mt-1">
                    <Building2 className="h-3 w-3" />
                    {member.department}
                  </div>
                )}
                
                {member.reports_to_name && (
                  <div className="text-xs text-muted-foreground mt-1">
                    Reports to: {member.reports_to_name}
                  </div>
                )}
                
                {member.overview && (
                  <p className="text-sm text-muted-foreground mt-3 line-clamp-2">{member.overview}</p>
                )}
                
                <div className="flex items-center justify-center gap-3 mt-4">
                  {member.email && (
                    <a href={`mailto:${member.email}`} className="text-muted-foreground hover:text-blue-500">
                      <Mail className="h-4 w-4" />
                    </a>
                  )}
                  {member.phone && (
                    <a href={`tel:${member.phone}`} className="text-muted-foreground hover:text-green-500">
                      <Phone className="h-4 w-4" />
                    </a>
                  )}
                  {member.linkedin_url && (
                    <a href={member.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-blue-600">
                      <Linkedin className="h-4 w-4" />
                    </a>
                  )}
                </div>
                
                <div className="flex items-center justify-center gap-2 mt-4 pt-4 border-t">
                  <Button variant="outline" size="sm" onClick={() => openEditDialog(member)}>
                    <Edit className="h-3 w-3 mr-1" />
                    Edit
                  </Button>
                  <Button variant="outline" size="sm" className="text-red-500 hover:text-red-600" onClick={() => setDeleteConfirm(member)}>
                    <Trash2 className="h-3 w-3 mr-1" />
                    Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingMember ? 'Edit Team Member' : 'Add Team Member'}</DialogTitle>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            {/* Profile Image */}
            <div className="flex items-center gap-4">
              <Avatar className="h-20 w-20">
                <AvatarImage src={getImageUrl(formData.profile_image_url)} />
                <AvatarFallback className="bg-indigo-100 text-indigo-600 text-xl">
                  {formData.name?.charAt(0) || 'U'}
                </AvatarFallback>
              </Avatar>
              <div>
                <input
                  type="file"
                  ref={fileInputRef}
                  className="hidden"
                  accept="image/*"
                  onChange={handleImageUpload}
                />
                <Button
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingImage}
                >
                  {uploadingImage ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Upload className="h-4 w-4 mr-2" />
                  )}
                  Upload Photo
                </Button>
                <p className="text-xs text-muted-foreground mt-1">Recommended: 200x200px</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Full name"
                />
              </div>
              <div className="space-y-2">
                <Label>Designation *</Label>
                <Input
                  value={formData.designation}
                  onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                  placeholder="e.g., Quality Lead"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="email@company.com"
                />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+1 555-0100"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Department</Label>
                <Input
                  value={formData.department}
                  onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                  placeholder="e.g., Quality Assurance"
                />
              </div>
              <div className="space-y-2">
                <Label>Reports To</Label>
                <Select
                  value={formData.reports_to || "none"}
                  onValueChange={(v) => setFormData({ ...formData, reports_to: v === "none" ? "" : v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select manager" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None (Top Level)</SelectItem>
                    {members
                      .filter(m => m.id !== editingMember?.id)
                      .map(m => (
                        <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>LinkedIn Profile URL</Label>
              <Input
                value={formData.linkedin_url}
                onChange={(e) => setFormData({ ...formData, linkedin_url: e.target.value })}
                placeholder="https://linkedin.com/in/username"
              />
            </div>

            <div className="space-y-2">
              <Label>Overview / Bio</Label>
              <Textarea
                value={formData.overview}
                onChange={(e) => setFormData({ ...formData, overview: e.target.value })}
                placeholder="Brief description about the team member..."
                rows={3}
              />
            </div>

            <div className="flex items-center gap-2">
              <Switch
                checked={formData.is_active}
                onCheckedChange={(v) => setFormData({ ...formData, is_active: v })}
              />
              <Label>Active</Label>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {editingMember ? 'Update' : 'Add'} Member
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Team Member?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {deleteConfirm?.name}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleDelete(deleteConfirm?.id)} className="bg-red-500 hover:bg-red-600">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
